import { client } from '../common/client';
import type {
  ProductsCollectionResponse,
  ProductsAllRequest,
  ProductsAllResponse,
  ProductsCollectionRequest,
  ProductsByIdsRequest,
  ProductsByIdsResponse,
  TopRankedProductsRequest,
  TopRankedProductsResponse,
  ProductTaxonomy,
  ProductBrandTaxonomy,
  ProductsCollectionCompetitorRequest,
  ProductsCollectionCompetitorResponse,
  CategoryTaxonomy,
  ProductBrowseRequest,
  ProductBrowseResponse,
  ProductsDetailRequest,
  ProductsDetailResponse,
  ProductMetaRequest,
  ProductMetaResponse,
  ProductSearchRequest,
  ProductSearchResponse,
  ListRecommendationsRequest,
  ListRecommendationsResponse,
  AIInsightRequest,
  AIInsightResponse,
  ProductsRecommendRequest,
  ProductsRecommendResponse,
  RoutineRecommendationsRequest,
  RoutineRecommendationsResponse,
} from './utils/types';
import { Currency, ProductStockStatus, ProductCategory } from '../../constants';

class ProductAPIClient {
  public async getProductsAll(params?: ProductsAllRequest): Promise<ProductsAllResponse> {
    return await client.get(`/products/all`, { params }).then((res) => res.data);
  }

  public async getProductsCollections(params?: ProductsCollectionRequest): Promise<ProductsCollectionResponse> {
    return await client.get(`/products/collection`, { params }).then((res) => res.data);
  }

  public async getProductsByIds(params: ProductsByIdsRequest): Promise<ProductsByIdsResponse> {
    return await client.get(`/products/byIds`, { params }).then((res) => res.data);
  }

  public async getProductsDetail(params: ProductsDetailRequest): Promise<ProductsDetailResponse> {
    return await client.get(`/page/productDetail`, { params }).then((res) => res.data);
  }

  public async getProductMeta(params: ProductMetaRequest): Promise<ProductMetaResponse> {
    const { productId, ...queryParams } = params;
    return await client.get(`/product/${productId}/meta`, { params: queryParams }).then((res) => res.data);
  }

  public async getTopRankedProducts(params?: TopRankedProductsRequest): Promise<TopRankedProductsResponse> {
    const response = await this.getProductsCollections({
      collectionName: 'Rankings by Product Category',
      categories: params?.categories,
      limit: params?.limit ?? 1000,
      currency: params?.currency ?? Currency.Singapore,
      page: 1,
    });

    return response;
  }

  public async getProductTaxonomy(): Promise<ProductTaxonomy> {
    return await client.get('/product/taxonomy').then((res) => res.data);
  }

  public async getProductBrandTaxonomy(): Promise<ProductBrandTaxonomy> {
    return await client.get('/product/taxonomy/brands').then((res) => res.data);
  }

  public async getProductsCollectionCompetitor(
    params: ProductsCollectionCompetitorRequest,
  ): Promise<ProductsCollectionCompetitorResponse> {
    const { competitorName, ...queryParams } = params;
    return await client
      .get(`/products/collection/competitor/${competitorName}`, {
        params: queryParams,
      })
      .then((res) => res.data);
  }

  public async getCategoryTaxonomy(): Promise<CategoryTaxonomy> {
    return await client.get('/product/taxonomy/categories').then((res) => res.data);
  }

  public async getAllCategoryTaxonomy(): Promise<CategoryTaxonomy> {
    return await client.get('/product/taxonomy/categories/all').then((res) => res.data);
  }

  public async getProductBrowse(params?: ProductBrowseRequest): Promise<ProductBrowseResponse> {
    const response = await client.get('/products/browse', { params });
    return response.data;
  }

  public async getProductSearch(params: ProductSearchRequest): Promise<ProductSearchResponse> {
    const response = await client.get('/products/search', { params });
    return response.data;
  }

  public async getListRecommendations(params: ListRecommendationsRequest): Promise<ListRecommendationsResponse> {
    const response = await client.get('/products/listRecommendations', { params });
    return response.data;
  }

  public async getAIInsight(params: AIInsightRequest): Promise<AIInsightResponse> {
    const response = await client.get('/products/aiInsight', { params });
    return response.data;
  }

  public async getRecommendedProducts(params: ProductsRecommendRequest): Promise<ProductsRecommendResponse> {
    const response = await client.get('/products/recommend', { params });
    return response.data;
  }

  public async getRoutineRecommendations(
    params: RoutineRecommendationsRequest,
  ): Promise<RoutineRecommendationsResponse> {
    const response = await client.get('/products/routineRecommendations', { params });
    return response.data;
  }
}

export const productAPIClient = new ProductAPIClient();
